import { del, get, keys, set } from "idb-keyval";

export const TRACKING_PRIORITY = 10;

export type OutboxItem = {
  id: string;
  priority: number;
  path: string;
  method: string;
  body: unknown;
  created_at: number;
};

const PREFIX = "outbox:";
const memory = new Map<string, OutboxItem>();
let useMemory = false;
let trackingPass: Promise<void> | null = null;

export function useMemoryOutbox(): void {
  useMemory = true;
  memory.clear();
  trackingPass = null;
}

export async function enqueue(item: OutboxItem): Promise<void> {
  if (useMemory) {
    memory.set(item.id, item);
    return;
  }
  await set(PREFIX + item.id, item);
}

export async function listOutbox(): Promise<OutboxItem[]> {
  if (useMemory) {
    return [...memory.values()].sort(byOrder);
  }
  const ks = await keys();
  const items: OutboxItem[] = [];
  for (const key of ks) {
    if (typeof key === "string" && key.startsWith(PREFIX)) {
      const value = await get<OutboxItem>(key);
      if (value) items.push(value);
    }
  }
  return items.sort(byOrder);
}

function byOrder(a: OutboxItem, b: OutboxItem): number {
  return a.priority - b.priority || a.created_at - b.created_at;
}

export async function removeOutbox(id: string): Promise<void> {
  if (useMemory) {
    memory.delete(id);
    return;
  }
  await del(PREFIX + id);
}

/**
 * Answers (priority below 10) are sent in order and stop at the first failure.
 * The returned promise resolves when that pass finishes. Tracking is a later
 * background pass, one at a time, and is not part of this promise.
 */
export async function syncOutboxPasses(opts: {
  sendAnswer: (item: OutboxItem) => Promise<boolean>;
  sendTracking: (item: OutboxItem) => Promise<"ok" | "stop">;
}): Promise<void> {
  const items = await listOutbox();
  for (const item of items) {
    if (item.priority >= TRACKING_PRIORITY) continue;
    const ok = await opts.sendAnswer(item);
    if (ok) await removeOutbox(item.id);
    else break;
  }
  startTrackingPass(opts.sendTracking);
}

/** Tracking rows only. A pass already in progress is left to finish. */
export function startTrackingPass(
  sendTracking: (item: OutboxItem) => Promise<"ok" | "stop">,
): void {
  if (trackingPass) return;
  trackingPass = new Promise((resolve) => {
    setTimeout(() => {
      void (async () => {
        const pending = (await listOutbox()).filter((item) => item.priority >= TRACKING_PRIORITY);
        for (const item of pending) {
          const result = await sendTracking(item);
          if (result === "stop") break;
        }
      })().finally(() => {
        trackingPass = null;
        resolve();
      });
    }, 0);
  });
}
